import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom'; // Import useParams
import SidebarFilter from 'components/artectum/sidebar-filters/sidebar-filters';
import ProductCardHome from 'components/artectum/cards/product-card-home';
import CategoryItem from '../cards/category-card';
import Slider from 'react-slick';
import { categoriesSliderSettings } from 'core/config/slides';
import { useProducts } from 'core/hooks/useProducts';
import { useFilters } from 'core/hooks/useFilters';
import { ProductGridPropTypes, PaginationPropTypes } from '../../../core/propTypes/gridPropTypes';
import QuickViewModal from 'components/artectum/modal/QuickViewModal';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import useFetch from 'core/hooks/api/useFetch';
import LoadingSpinner from 'components/common/LoadingSpinner'; // Importar el componente de carga

const ProductGrid = React.memo(({ products, onQuickView }) => {
	const publicUrl =  "/";

	return (
		<div className="row">
			{products?.map((product, index) => (
				<div className="col-xl-4 col-sm-6 col-12 p-0" key={index}>
					{/* {product?.photos && product?.photos?.length > 0 && ( */}
						<ProductCardHome item={product} publicUrl={publicUrl} onQuickView={onQuickView} />
					{/* )} */}
				</div>
			))}
		</div>
	);
});

const Pagination = React.memo(({ currentPage, pageCount, onPageChange }) => (
	<div className="ltn__pagination-area text-center">
		<div className="ltn__pagination">
			<ul>
				<li><Link to="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); onPageChange(1); }}>
					<i className="fas fa-angle-double-left" />
				</Link></li>
				{[...Array(pageCount)]?.map((_, i) => (
					<li key={i + 1} className={currentPage === i + 1 ? 'active' : ''}>
						<Link to="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); onPageChange(i + 1); }}>
							{i + 1}
						</Link>
					</li>
				))}
				<li><Link to="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); onPageChange(pageCount); }}>
					<i className="fas fa-angle-double-right" />
				</Link></li>
			</ul>
		</div>
	</div>
));

const formatCategoryValue = (value) => {
    return value.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const GridProduct = ({ customerClass }) => {
    const location = useLocation();
    const { id } = useParams(); // Get the category ID from the URL parameters
    const [selectedFilters, setSelectedFilters] = useState({});
    const { data: productsData, error, loading: loadingProduct } = useFetch(`${API_ENDPOINTS.PRODUCTS}/category/${id}?${new URLSearchParams(selectedFilters).toString()}`); // Update the API endpoint
    const { data: productsFilter } = useFetch(API_ENDPOINTS.PRODUCT_FILTERS);
    const { data: filter } = useFetch(API_ENDPOINTS.FILTERS);
    const { data: productCategories, isLoading, error: productCategoriesError } = useFetch(API_ENDPOINTS.PRODUCTS + "/category/" + id);
    const { currentProducts, currentPage, pageCount, setCurrentPage } = useProducts(productsData);

    const validFilters = useMemo(() => filter?.filter(f => productsFilter?.some(pf => pf.filter_id === f.id)), [filter, productsFilter]);

    const { filters, selectedCategory, setSelectedCategory, handleFilterChange, setFilters } = useFilters(validFilters);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleQuickView = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    const handleModalClose = () => setShowModal(false);

    const searchStats = {
        resultCount: productsData ? productsData?.total : 0,
        searchTime: 0.62
    };

    const handlePageChange = (page) => {
        if (page !== currentPage) {
            setCurrentPage(page);
            setSelectedFilters(prevFilters => ({
                ...prevFilters,
                page: page
            }));
        }
    };

    useEffect(() => {
        if (Array.isArray(productsData)) {
            setCurrentPage(1);
        }
    }, [productsData, productsFilter, filter, id]); // Add id to the dependency array

    useEffect(() => {
        if (Array.isArray(productsData)) {
            const startIndex = (currentPage - 1) * 10; // Assuming 10 products per page
            const endIndex = startIndex + 10;
        }
    }, [currentPage, productsData]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const initialFilters = {};

        params.forEach((value, key) => {
            if (!key.toLowerCase().includes('utm')) {
                initialFilters[`filters[${key}]`] = value;
            }
        });

        setSelectedFilters(initialFilters);

        // Marcar filtros activos y expandir secciones
        const updatedFilters = validFilters?.map(f => {
            const filterKey = `filters[${f.name.toLowerCase()}]`;
            if (initialFilters[filterKey]) {
                const values = initialFilters[filterKey].split(',');
                return {
                    ...f,
                    options: f.options?.map(option => ({
                        ...option,
                        checked: values.includes(option.value)
                    })),
                    defaultOpen: true
                };
            }
            return f;
        });

        setFilters(updatedFilters);
    }, [location.search, validFilters, setFilters, id]);

    const handleFilterChangeWithRefetch = (sectionTitle, value, checked, type) => {
        handleFilterChange(sectionTitle, value, checked);
        setSelectedFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            const filterKey = `filters[${sectionTitle.toLowerCase()}]`;
            const currentValues = newFilters[filterKey] ? newFilters[filterKey].split(',') : [];

            if (type === 'single') {
                if (checked) {
                    newFilters[filterKey] = value;
                } else {
                    delete newFilters[filterKey];
                }
            } else {
                if (checked) {
                    currentValues.push(value);
                } else {
                    const index = currentValues.indexOf(value);
                    if (index > -1) {
                        currentValues.splice(index, 1);
                    }
                }

                if (currentValues.length > 0) {
                    newFilters[filterKey] = currentValues.join(',');
                } else {
                    delete newFilters[filterKey];
                }
            }

            return newFilters;
        });
    };

    const handleClearFilters = () => {
        setSelectedFilters({});
        setFilters(validFilters);
    };

    return (
        <div className={`ltn__product-area ltn__product-gutter ${customerClass}`}>
            <div className="container">
                {error && <p>Error loading products</p>}
                {productCategoriesError && <p>Error loading categories</p>}
                <div className="row mt-5" style={{ position: 'relative' }}>
                    <div className="col-lg-9 order-lg-2 mb-100" style={{ minHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                        {/* <div className="ltn__shop-options mb-0 bg-white p-0">
                            <div className="col-12 mb-2 p-0">
                                <h6 className="fw-bold mb-3 pt-3">¿Qué estas buscando?</h6>
                                <Slider {...categoriesSliderSettings} className="slick-arrow-5 hidden-mob">
                                    {productCategories ? (
                                        productCategories[0]?.options?.map((category) => (
                                            <CategoryItem
                                                key={category.id}
                                                category={{ ...category, value: formatCategoryValue(category.value) }}
                                                isSelected={selectedCategory === category.value}
                                                onSelect={setSelectedCategory}
                                            />
                                        ))
                                    ) : (
                                        [...Array(7)].map((_, index) => (
                                            <div className="col-md-3 col-sm-6 col-6" key={index}>
                                                <PlaceholderCategoryItem />
                                            </div>
                                        ))
                                    )}
                                </Slider>
                            </div>
                        </div> */}

                        <div className="tab-content">
                            <div className="tab-pane fade active show" id="liton_product_grid">
                                <div className="ltn__product-tab-content-inner ltn__product-grid-view">
                                    {isLoading || loadingProduct ? (
                                        <LoadingSpinner /> // Mostrar el componente de carga mientras se cargan los productos
                                    ) : (
                                        <ProductGrid products={currentProducts} onQuickView={handleQuickView} />
                                    )}
                                </div>
                            </div>
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                        />
                    </div>
                    <SidebarFilter
                        filters={filters}
                        searchStats={searchStats}
                        onFilterChange={(sectionTitle, value, checked) => handleFilterChangeWithRefetch(sectionTitle, value, checked, filters.find(f => f.name === sectionTitle).type)}
                        title="Filtros de Búsqueda"
                        columnSize="col-lg-3"
                        onClearFilters={handleClearFilters}
                    />
                </div>
            </div>
            {selectedProduct && (
                <QuickViewModal
                    show={showModal}
                    product={selectedProduct}
                    onHide={handleModalClose}
                    item={selectedProduct}
                />
            )}
        </div>
    );
};

ProductGrid.propTypes = ProductGridPropTypes;
Pagination.propTypes = PaginationPropTypes;

export default GridProduct;